/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

/**
 * @fileoverview Toolbox category with styling for continuous toolbox.
 */

import * as Blockly from 'blockly/core'

const makeIcon = (width, height, paths) => {
  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  icon.setAttributeNS('http://www.w3.org/1999/xlink', 'width', width)
  icon.setAttributeNS('http://www.w3.org/1999/xlink', 'height', height)
  icon.setAttributeNS(
    'http://www.w3.org/1999/xlink',
    'viewBox',
    `0 0 ${width} ${height}`,
  )
  icon.setAttributeNS('http://www.w3.org/1999/xlink', 'fill', 'none')
  for (const path of paths) {
    Blockly.utils.dom.createSvgElement(
      Blockly.utils.Svg.PATH,
      {
        fill: 'none',
        ...path,
      },
      icon,
    )
  }
  icon.style.width = `${width}px`
  icon.style.height = `${height}px`
  return icon
}

const ICONS = {
  control: () =>
    makeIcon(24, 24, [
      {
        d: 'M16 12C16 11.2089 15.7654 10.4355 15.3259 9.77772C14.8864 9.11992 14.2616 8.60723 13.5307 8.30448C12.7998 8.00173 11.9956 7.92252 11.2196 8.07686C10.4437 8.2312 9.73098 8.61216 9.17157 9.17157C8.61216 9.73098 8.2312 10.4437 8.07686 11.2196C7.92252 11.9956 8.00173 12.7998 8.30448 13.5307C8.60723 14.2616 9.11992 14.8864 9.77772 15.3259C10.4355 15.7654 11.2089 16 12 16C13.0605 15.9988 14.0772 15.577 14.8271 14.8271C15.577 14.0772 15.9988 13.0605 16 12ZM9.6 12C9.6 11.5253 9.74076 11.0613 10.0045 10.6666C10.2682 10.272 10.643 9.96434 11.0816 9.78269C11.5201 9.60104 12.0027 9.55351 12.4682 9.64612C12.9338 9.73872 13.3614 9.9673 13.6971 10.3029C14.0327 10.6386 14.2613 11.0662 14.3539 11.5318C14.4465 11.9973 14.399 12.4799 14.2173 12.9184C14.0357 13.357 13.728 13.7318 13.3334 13.9955C12.9387 14.2592 12.4747 14.4 12 14.4C11.3637 14.3993 10.7537 14.1462 10.3037 13.6963C9.85382 13.2463 9.60073 12.6363 9.6 12ZM7.2 19.0994L11.4654 24H12.5346L16.8 19.0994V17.6H7.2V19.0994ZM14.5913 19.2L12 22.1772L9.40875 19.2H14.5913ZM11.4654 0L7.2 4.9006V6.4H16.8V4.9006L12.5346 0H11.4654ZM9.40875 4.8L12 1.8228L14.5913 4.8H9.40875ZM0 11.4654V12.5346L4.90065 16.8H6.4V7.2H4.90065L0 11.4654ZM4.8 14.5913L1.82275 12L4.8 9.4087V14.5913ZM19.0993 7.2H17.6V16.8H19.0993L24 12.5346V11.4654L19.0993 7.2ZM19.2 14.5913V9.4087L22.1773 12L19.2 14.5913Z',
        fill: 'currentColor',
      },
    ]),
  events: () =>
    makeIcon(25, 27, [
      {
        d: 'M23 25V22.4444C23 21.0889 22.4469 19.7889 21.4623 18.8303C20.4777 17.8718 19.1424 17.3333 17.75 17.3333H16.4375C16.0894 17.3333 15.7556 17.1987 15.5094 16.9591C15.2633 16.7195 15.125 16.3944 15.125 16.0556V9.66667C15.125 8.98889 14.8484 8.33888 14.3562 7.85962C13.8639 7.38036 13.1962 7.11111 12.5 7.11111C11.8038 7.11111 11.1361 7.38036 10.6438 7.85962C10.1516 8.33888 9.875 8.98889 9.875 9.66667V21.1667L6.725 17.0778C6.48049 16.7604 6.16343 16.5028 5.79894 16.3254C5.43444 16.1479 5.03252 16.0556 4.625 16.0556H4.05537C2.92006 16.0556 2 16.9513 2 18.0566C2 18.4194 2.10106 18.7759 2.29269 19.0864L5.9375 25M12.5 3.27778V2M20.375 10.9444H21.6875M3.3125 10.9444H4.625M6.38769 4.99383L5.45975 4.09044M18.6123 4.99383L19.5403 4.09044',
        stroke: 'currentColor',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2.66667',
      },
    ]),
  looks: () =>
    makeIcon(24, 25, [
      {
        d: 'M19.0245 16.8153C20.3191 11.9841 17.2537 6.99419 12.2179 5.64502C6.53104 4.11977 -1.5404 8.08047 0.255181 13.1609C0.850402 14.8412 1.9962 15.2343 4.1576 15.2653L4.29276 15.2678C5.43484 15.2814 5.75601 15.3558 5.85274 15.5121C6.01022 15.77 6.0127 16.1804 5.83538 17.3101C5.75105 17.8433 5.72377 18.0293 5.69153 18.3207C5.49437 20.0953 5.79446 21.4791 6.95638 22.7316C10.5872 26.6414 17.5625 22.269 19.0245 16.8153ZM2.5939 12.3326C1.68991 9.77809 7.47472 6.93963 11.5755 8.03831C15.3168 9.0415 17.56 12.6934 16.6287 16.1718C15.5896 20.0506 10.6988 23.116 8.77428 21.0426C8.17906 20.4028 8.0315 19.7183 8.15674 18.5936C8.18278 18.353 8.20758 18.1868 8.2857 17.6933C8.55727 15.9609 8.55355 15.1698 7.96577 14.2137C7.22919 13.0146 6.35372 12.8125 4.32376 12.7864L4.19232 12.784C2.99692 12.7678 2.71419 12.6699 2.5939 12.3326Z',
        fill: 'currentColor',
        fillRule: 'evenodd',
        clipRule: 'evenodd',
      },
      {
        d: 'M6.3512 11.9098C5.9401 11.9098 5.54584 11.7465 5.25515 11.4558C4.96446 11.1651 4.80115 10.7709 4.80115 10.3598C4.80115 9.94868 4.96446 9.55442 5.25515 9.26372C5.54584 8.97303 5.9401 8.80972 6.3512 8.80972C6.7623 8.80972 7.15656 8.97303 7.44726 9.26372C7.73795 9.55442 7.90126 9.94868 7.90126 10.3598C7.90126 10.7709 7.73795 11.1651 7.44726 11.4558C7.15656 11.7465 6.7623 11.9098 6.3512 11.9098ZM11.0014 11.9098C10.5903 11.9098 10.196 11.7465 9.90531 11.4558C9.61462 11.1651 9.45131 10.7709 9.45131 10.3598C9.45131 9.94868 9.61462 9.55442 9.90531 9.26372C10.196 8.97303 10.5903 8.80972 11.0014 8.80972C11.4125 8.80972 11.8067 8.97303 12.0974 9.26372C12.3881 9.55442 12.5514 9.94868 12.5514 10.3598C12.5514 10.7709 12.3881 11.1651 12.0974 11.4558C11.8067 11.7465 11.4125 11.9098 11.0014 11.9098ZM14.1015 15.63C13.6904 15.63 13.2961 15.4667 13.0054 15.176C12.7147 14.8853 12.5514 14.491 12.5514 14.0799C12.5514 13.6688 12.7147 13.2745 13.0054 12.9839C13.2961 12.6932 13.6904 12.5299 14.1015 12.5299C14.5126 12.5299 14.9068 12.6932 15.1975 12.9839C15.4882 13.2745 15.6515 13.6688 15.6515 14.0799C15.6515 14.491 15.4882 14.8853 15.1975 15.176C14.9068 15.4667 14.5126 15.63 14.1015 15.63ZM12.2414 19.9701C11.8303 19.9701 11.436 19.8068 11.1454 19.5161C10.8547 19.2254 10.6914 18.8312 10.6914 18.4201C10.6914 18.009 10.8547 17.6147 11.1454 17.324C11.436 17.0333 11.8303 16.87 12.2414 16.87C12.6525 16.87 13.0468 17.0333 13.3375 17.324C13.6282 17.6147 13.7915 18.009 13.7915 18.4201C13.7915 18.8312 13.6282 19.2254 13.3375 19.5161C13.0468 19.8068 12.6525 19.9701 12.2414 19.9701ZM16.7304 3.97975C16.8774 3.90098 17.0388 3.85268 17.205 3.83774C17.3711 3.82281 17.5385 3.84155 17.6973 3.89283C17.856 3.94412 18.0027 4.02691 18.1287 4.13623C18.2547 4.24556 18.3573 4.37918 18.4305 4.52909L23.506 14.9455C23.5907 15.1327 23.601 15.3452 23.5347 15.5397C23.4684 15.7342 23.3305 15.8962 23.149 15.9927C22.9675 16.0892 22.7561 16.113 22.5578 16.0592C22.3594 16.0054 22.189 15.8781 22.0812 15.7031L16.243 5.69597C16.1591 5.55202 16.1051 5.39259 16.0843 5.22726C16.0635 5.06193 16.0763 4.8941 16.1219 4.73384C16.1675 4.57357 16.2451 4.42417 16.3498 4.29459C16.4546 4.16501 16.5844 4.05793 16.7316 3.97975H16.7304Z',
        fill: 'currentColor',
      },
      {
        d: 'M14.4576 3.82963C15.0615 4.96427 16.0771 5.28172 17.0592 4.75966C18.04 4.23885 18.3463 3.21829 17.7424 2.08365C17.053 0.784084 15.5748 -0.393957 14.5952 0.125621C13.6156 0.646439 13.7669 2.53007 14.4588 3.82963H14.4576ZM15.5525 3.24681C15.3329 2.8059 15.1983 2.32759 15.1557 1.83688C15.1373 1.63174 15.1447 1.42509 15.178 1.22182L15.2003 1.23298C15.2623 1.26274 15.4546 1.35202 15.7001 1.54795C16.0746 1.8468 16.4466 2.28702 16.6475 2.66523C16.9314 3.19845 16.8508 3.4663 16.4763 3.66595C16.1043 3.86435 15.8365 3.78003 15.5525 3.24805V3.24681Z',
        fill: 'currentColor',
        fillRule: 'evenodd',
        clipRule: 'evenodd',
      },
    ]),
  movement: () =>
    makeIcon(27, 14, [
      {
        d: 'M8 13.3333C7.62222 13.3333 7.30533 13.2053 7.04933 12.9493C6.79422 12.6942 6.66667 12.3778 6.66667 12C6.66667 11.6222 6.79422 11.3053 7.04933 11.0493C7.30533 10.7942 7.62222 10.6667 8 10.6667H14.6667C14.3556 10.2889 14.1058 9.87778 13.9173 9.43333C13.728 8.98889 13.5778 8.51111 13.4667 8H10.6667C10.2889 8 9.97244 7.872 9.71733 7.616C9.46133 7.36089 9.33333 7.04444 9.33333 6.66667C9.33333 6.28889 9.46133 5.972 9.71733 5.716C9.97244 5.46089 10.2889 5.33333 10.6667 5.33333H13.4667C13.5778 4.82222 13.728 4.34444 13.9173 3.9C14.1058 3.45556 14.3556 3.04444 14.6667 2.66667H2.66667C2.28889 2.66667 1.97244 2.53867 1.71733 2.28267C1.46133 2.02756 1.33333 1.71111 1.33333 1.33333C1.33333 0.955556 1.46133 0.638667 1.71733 0.382667C1.97244 0.127556 2.28889 0 2.66667 0H20C21.8444 0 23.4169 0.649778 24.7173 1.94933C26.0169 3.24978 26.6667 4.82222 26.6667 6.66667C26.6667 8.51111 26.0169 10.0831 24.7173 11.3827C23.4169 12.6831 21.8444 13.3333 20 13.3333H8ZM20 10.6667C21.1111 10.6667 22.0556 10.2778 22.8333 9.5C23.6111 8.72222 24 7.77778 24 6.66667C24 5.55556 23.6111 4.61111 22.8333 3.83333C22.0556 3.05556 21.1111 2.66667 20 2.66667C18.8889 2.66667 17.9444 3.05556 17.1667 3.83333C16.3889 4.61111 16 5.55556 16 6.66667C16 7.77778 16.3889 8.72222 17.1667 9.5C17.9444 10.2778 18.8889 10.6667 20 10.6667ZM1.33333 8C0.955556 8 0.639111 7.872 0.384 7.616C0.128 7.36089 0 7.04444 0 6.66667C0 6.28889 0.128 5.972 0.384 5.716C0.639111 5.46089 0.955556 5.33333 1.33333 5.33333H6.66667C7.04444 5.33333 7.36133 5.46089 7.61733 5.716C7.87244 5.972 8 6.28889 8 6.66667C8 7.04444 7.87244 7.36089 7.61733 7.616C7.36133 7.872 7.04444 8 6.66667 8H1.33333ZM2.66667 13.3333C2.28889 13.3333 1.97244 13.2053 1.71733 12.9493C1.46133 12.6942 1.33333 12.3778 1.33333 12C1.33333 11.6222 1.46133 11.3053 1.71733 11.0493C1.97244 10.7942 2.28889 10.6667 2.66667 10.6667H4C4.37778 10.6667 4.69467 10.7942 4.95067 11.0493C5.20578 11.3053 5.33333 11.6222 5.33333 12C5.33333 12.3778 5.20578 12.6942 4.95067 12.9493C4.69467 13.2053 4.37778 13.3333 4 13.3333H2.66667Z',
        fill: 'currentColor',
      },
    ]),
  operators: () =>
    makeIcon(23, 22, [
      {
        d: 'M21.85 0.699951H1.15C0.84511 0.700306 0.552809 0.821581 0.337219 1.03717C0.121629 1.25276 0.000355084 1.54506 0 1.84995V20.25C0.000355084 20.5548 0.121629 20.8471 0.337219 21.0627C0.552809 21.2783 0.84511 21.3996 1.15 21.4H21.85C22.1549 21.3996 22.4472 21.2783 22.6628 21.0627C22.8784 20.8471 22.9996 20.5548 23 20.25V1.84995C22.9996 1.54506 22.8784 1.25276 22.6628 1.03717C22.4472 0.821581 22.1549 0.700306 21.85 0.699951ZM21.4667 19.8666H1.53333V2.23328H21.4667V19.8666Z',
        fill: 'currentColor',
      },
      {
        d: 'M6.51689 10.2833H8.05022V8.36663H9.96689V6.83329H8.05022V4.91663H6.51689V6.83329H4.60022V8.36663H6.51689V10.2833ZM8.63883 12.4438L7.28355 13.7991L5.92828 12.4438L4.84407 13.528L6.19934 14.8833L4.84407 16.2386L5.92828 17.3228L7.28355 15.9675L8.63883 17.3228L9.72304 16.2386L8.36776 14.8833L9.72304 13.528L8.63883 12.4438ZM13.0336 6.83329H18.4002V8.36663H13.0336V6.83329ZM13.0336 12.5833H18.4002V14.1166H13.0336V12.5833ZM13.0336 15.65H18.4002V17.1833H13.0336V15.65Z',
        fill: 'currentColor',
      },
    ]),
  procedures: () =>
    makeIcon(23, 23, [
      {
        d: 'M2 14.6667H8.33333M2 14.6667V21H8.33333V14.6667M2 14.6667V8.33333H8.33333M8.33333 14.6667H14.6667M8.33333 14.6667V8.33333M21 8.33333V2H14.6667V8.33333M21 8.33333H14.6667M21 8.33333V14.6667H14.6667M14.6667 8.33333V14.6667M14.6667 8.33333H8.33333',
        stroke: 'currentColor',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2.66667',
      },
    ]),
  sensing: () =>
    makeIcon(23, 23, [
      {
        d: 'M16.0436 16.0435L21.0001 21',
        stroke: 'currentColor',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '3',
      },
      {
        d: 'M9.84783 17.6957C14.1821 17.6957 17.6957 14.1821 17.6957 9.84783C17.6957 5.51359 14.1821 2 9.84783 2C5.51359 2 2 5.51359 2 9.84783C2 14.1821 5.51359 17.6957 9.84783 17.6957Z',
        stroke: 'currentColor',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '3',
      },
    ]),
  sound: () =>
    makeIcon(29, 23, [
      {
        d: 'M19.9723 16.6442C19.6644 16.6442 19.348 16.5632 19.0603 16.3925C18.2058 15.8834 17.921 14.7756 18.4274 13.9166C19.3911 12.2737 19.3911 10.2374 18.4274 8.59739C17.921 7.73544 18.2058 6.62763 19.0603 6.11857C19.9234 5.61528 21.0224 5.89874 21.5229 6.7549C23.1542 9.53164 23.1542 12.9794 21.5229 15.7562C21.1892 16.3289 20.5879 16.6442 19.9723 16.6442ZM25.3433 19.545C25.0355 19.545 24.7162 19.464 24.4314 19.2933C23.5769 18.7843 23.2921 17.6765 23.7984 16.8174C25.8152 13.387 25.8152 9.12352 23.7984 5.69598C23.2921 4.83693 23.5769 3.72623 24.4314 3.21716C25.2887 2.71677 26.3877 2.99733 26.894 3.8535C29.5811 8.41777 29.5811 14.0927 26.894 18.657C26.5574 19.2297 25.959 19.545 25.3433 19.545ZM15.3654 2.41075V20.0864C15.3654 22.1806 12.897 23.2768 11.3578 21.8653L8.03494 18.8138C6.85827 17.7349 5.32486 17.1362 3.73104 17.1362H3.14989C1.56182 17.1362 0.272949 15.8432 0.272949 14.2437V8.28529C0.272949 6.68867 1.56182 5.39285 3.14989 5.39285H3.69939C5.29321 5.39285 6.82663 4.79412 8.0033 3.71524L11.3578 0.63479C12.897 -0.776721 15.3654 0.319513 15.3654 2.41075Z',
        fill: 'currentColor',
        fillRule: 'evenodd',
        clipRule: 'evenodd',
      },
    ]),
  variables: () =>
    makeIcon(21, 21, [
      {
        d: 'M21 7H14V0H21V7ZM15.75 5.25H19.25V1.75H15.75V5.25Z',
        fill: 'currentColor',
      },
      {
        d: 'M11.375 9.625V4.375H4.375V16.625H16.625V9.625H11.375ZM6.125 6.125H9.625V9.625H6.125V6.125ZM9.625 14.875H6.125V11.375H9.625V14.875ZM14.875 14.875H11.375V11.375H14.875V14.875Z',
        fill: 'currentColor',
      },
      {
        d: 'M19.25 21H1.75C1.28601 20.9995 0.841163 20.815 0.513075 20.4869C0.184987 20.1588 0.000463292 19.714 0 19.25V1.75C0.000463292 1.28601 0.184987 0.841163 0.513075 0.513075C0.841163 0.184987 1.28601 0.000463292 1.75 0H10.5V1.75H1.75V19.25H19.25V10.5H21V19.25C20.9995 19.714 20.815 20.1588 20.4869 20.4869C20.1588 20.815 19.714 20.9995 19.25 21Z',
        fill: 'currentColor',
      },
    ]),
}

/** Toolbox category for continuous toolbox. */
export class ContinuousCategory extends Blockly.ToolboxCategory {
  /**
   * Constructor for ContinuousCategory which is used in ContinuousToolbox.
   * @override
   */
  constructor(categoryDef, toolbox) {
    super(categoryDef, toolbox)
  }

  /** @override */
  createLabelDom_(name) {
    const label = document.createElement('div')
    label.setAttribute('id', this.getId() + '.label')
    label.textContent = name
    label.classList.add(this.cssConfig_['label'])
    return label
  }

  /** @override */
  createIconDom_() {
    const elem = document.createElement('div')
    elem.classList.add(this.cssConfig_['icon'])
    if (ICONS.hasOwnProperty(this.toolboxItemDef_.categoryStyle)) {
      elem.appendChild(ICONS[this.toolboxItemDef_.categoryStyle]())
    }
    return elem
  }

  /** @override */
  addColourBorder_() {
    this.rowDiv_.style.color = 'white'
    this.rowDiv_.style.backgroundColor = this.colour_
    Blockly.utils.dom.addClass(
      this.rowDiv_,
      `category-${this.toolboxItemDef_.categoryStyle}`,
    )
  }

  /** @override */
  setSelected(isSelected) {
    if (isSelected) {
      this.rowDiv_.style.color = this.colour_
      this.rowDiv_.style.backgroundColor = 'white'
      Blockly.utils.dom.addClass(this.rowDiv_, this.cssConfig_['selected'])
    } else {
      this.rowDiv_.style.color = 'white'
      this.rowDiv_.style.backgroundColor = this.colour_
      Blockly.utils.dom.removeClass(this.rowDiv_, this.cssConfig_['selected'])
    }
    Blockly.utils.aria.setState(
      /** @type {!Element} */ (this.htmlDiv_),
      Blockly.utils.aria.State.SELECTED,
      isSelected,
    )
  }
}

Blockly.registry.register(
  Blockly.registry.Type.TOOLBOX_ITEM,
  Blockly.ToolboxCategory.registrationName,
  ContinuousCategory,
  true,
)
